import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import socketService from '../api/socket';
import creditService from '../api/services/creditService';

/**
 * État global du solde de crédits de l'organisation.
 *
 * Source de vérité = le backend. On n'effectue JAMAIS de calcul local : la
 * valeur affichée provient soit de GET /credits/balance, soit du `balance`
 * porté par l'événement socket `credits:updated` (room `org:{id}`).
 *
 * Cet événement est émis à CHAQUE débit et CHAQUE recharge — y compris pour les
 * débits déclenchés en arrière-plan par les messages WhatsApp traités par l'IA
 * (aucune action de l'utilisateur dans le dashboard). C'est ce qui rend la
 * consommation visible « au fil de l'utilisation ».
 *
 * Robustesse :
 *  - reconnexion socket → on refait un GET /credits/balance (rattrapage des
 *    events manqués pendant la coupure) ;
 *  - retour d'onglet / focus fenêtre → idem (filet de sécurité).
 */
const CreditsContext = createContext(null);

export function CreditsProvider({ children }) {
  // null = pas encore chargé (évite d'afficher « 0 » avant le premier fetch).
  const [balance, setBalance] = useState(null);
  const [actionCosts, setActionCosts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dernier événement temps réel : alimente la micro-animation du badge.
  // `id` est un compteur unique pour rejouer l'animation même si `change`
  // est identique à l'event précédent (ex. deux débits -1 d'affilée).
  const [lastEvent, setLastEvent] = useState(null);
  const eventIdRef = useRef(0);

  const fetchBalance = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await creditService.getBalance();
      const data = res?.data || {};
      if (typeof data.balance === 'number') setBalance(data.balance);
      if (data.actionCosts) setActionCosts(data.actionCosts);
      setError(null);
    } catch (err) {
      setError(err?.error?.message || err?.message || 'Solde de crédits indisponible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setLoading(false);
      return undefined;
    }

    // Réutilise la connexion socket partagée (rejoint déjà la room org au connect).
    if (!socketService.isConnected()) {
      socketService.connect(token);
    }

    // 1. Chargement initial du solde + barème.
    fetchBalance();

    // 2. Mise à jour temps réel : on prend `payload.balance` tel quel.
    const handleCreditsUpdated = (payload) => {
      if (!payload || typeof payload.balance !== 'number') return;
      setBalance(payload.balance);
      eventIdRef.current += 1;
      setLastEvent({
        id: eventIdRef.current,
        change: typeof payload.change === 'number' ? payload.change : 0,
        type: payload.type,
        action: payload.action ?? null,
        depleted: !!payload.depleted,
        at: payload.at,
      });
    };

    // 3. (Re)connexion : le socket émet `connect` à chaque reconnexion ;
    //    on resynchronise alors le solde pour rattraper les events manqués.
    const handleReconnect = () => {
      fetchBalance();
    };

    // 4. Filet de sécurité : retour d'onglet / focus fenêtre.
    const handleFocus = () => fetchBalance();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchBalance();
    };

    socketService.on('credits:updated', handleCreditsUpdated);
    socketService.on('connect', handleReconnect);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      socketService.off('credits:updated', handleCreditsUpdated);
      socketService.off('connect', handleReconnect);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchBalance]);

  // Épuisé = solde chargé et à zéro (les agents sont alors en pause côté serveur).
  const depleted = balance !== null && balance <= 0;

  const value = useMemo(
    () => ({ balance, actionCosts, loading, error, lastEvent, depleted, refetch: fetchBalance }),
    [balance, actionCosts, loading, error, lastEvent, depleted, fetchBalance]
  );

  return <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>;
}

export function useCredits() {
  const ctx = useContext(CreditsContext);
  if (!ctx) {
    throw new Error('useCredits doit être utilisé à l’intérieur de <CreditsProvider>');
  }
  return ctx;
}

export default CreditsContext;
